<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { onDestroy, untrack } from 'svelte';
    import settingsKeys from '../../../settings/settingsKeys.js';
    import ControlRow from './generic/ControlRow.svelte';
    import { __ } from '@oat-sa-private/ui-core';

    /**
     * @typedef {Object} Props
     * @property {Object} areaBroker
     * @property {Object} initialState
     * @property {Function} onChange
     */
    /** @type {Props} */
    // prettier-ignore
    let {
        areaBroker,
        initialState,
        onChange
    } = $props();

    // svelte-ignore state_referenced_locally
    const targetElt = areaBroker?.getContainer();

    // Get word-spacing from letter-spacing: the ratio used is from typographic best practices
    const getWordSpacingValue = letterSpacingValue => letterSpacingValue * 1.33;

    // stepper props
    // these values represent the letter-spacing ems
    const min = 0;
    const step = 0.0625; // 1px
    const max = 0.25; // 4px
    let value = $state(0);

    const initialStateOnce = untrack(() => initialState);
    if (initialStateOnce?.value) {
        handleLetterAndWordSpacingChange(initialStateOnce);
    }
    let nonDefault = initialStateOnce?.nonDefault;

    /**
     * Handle the input being changed
     * @param {Object} [newState]
     */
    function handleLetterAndWordSpacingChange(newState = {}) {
        value = newState?.value;
        const letterSpacingValue = `${value}em`;
        const wordSpacingValue = `${getWordSpacingValue(value)}em`;

        if (targetElt) {
            if (value !== min) {
                targetElt.dataset.a11yOverrideSpacing = 'true';
            } else {
                delete targetElt.dataset.a11yOverrideSpacing;
            }

            targetElt.style.setProperty('--letter-spacing', letterSpacingValue);
            targetElt.style.setProperty('--word-spacing', wordSpacingValue);
        }

        onChange?.({
            key: settingsKeys.letterAndWordSpacing,
            state: {
                value,
                letterSpacingValue,
                wordSpacingValue,
                nonDefault: newState?.nonDefault
            }
        });
    }

    onDestroy(() => {
        delete targetElt?.dataset.a11yOverrideSpacing;

        targetElt?.style.removeProperty('--letter-spacing');
        targetElt?.style.removeProperty('--word-spacing');
    });
</script>

<ControlRow
    icon="font-spacing-16"
    label={__('Spacing')}
    {min}
    {max}
    {step}
    {value}
    {nonDefault}
    ariaLabelDecr={__('decrease letter and word spacing')}
    ariaLabelIncr={__('increase letter and word spacing')}
    onChange={handleLetterAndWordSpacingChange} />
